// pagina Inicio de sesion. (logotipo y fields de loggear)

import React, { useEffect, useState, useRef } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useUser } from '../UserContext';
import { emailValidator } from '../validators';

const Login = ({ onNavigate }) => {
  const { setUser } = useUser();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openForgot, setOpenForgot] = useState(false);
  const [recoverEmail, setRecoverEmail] = useState('');
  const [recoverMessage, setRecoverMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileWidgetId, setTurnstileWidgetId] = useState(null);
  const captchaRef = useRef(null);

  // Desactivar Turnstile en modo desarrollo/localhost
  const disableTurnstile = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost' || process.env.REACT_APP_DISABLE_TURNSTILE === 'true';

  if (disableTurnstile && window.turnstile) {
    turnstile.remove();
  };

  useEffect(() => {
    if (disableTurnstile) {
      console.log('Turnstile is disabled in development');
      return;
    }
    const renderCaptcha = () => {
      if (window.turnstile && captchaRef.current) {
        const widgetId = window.turnstile.render(captchaRef.current, {
          sitekey: "0x4AAAAAAFCG65tETcJ8IvqN",
          callback: (token) => setTurnstileToken(token),
        });
        setTurnstileWidgetId(widgetId);
      }
    };

    if (window.turnstile) {
      renderCaptcha();
    } else {
      // Esperar a que Turnstile esté disponible
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkTurnstile);
          renderCaptcha();
        }
      }, 100);
    }
  }, [disableTurnstile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [e.target.name]: e.target.value });

    if (name === 'email') {
      const err = emailValidator(value);
      if (err) {
        setError(err);
      } else {
        setError(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Verificar si el captcha está completado (no en modo desarrollo)
    if (!disableTurnstile && !turnstileToken) {
      setError('Por favor completa el captcha antes de iniciar sesión');
      setLoading(false);
      return;
    }

    try {
      const requestBody = {
        ...form,
        ...(disableTurnstile ? {} : { turnstileToken })
      };

      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      if (!res.ok) {
        let errorMessage = 'Correo o contraseña incorrectos';
        try {
          const errorData = await res.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {}
        throw new Error(errorMessage);
      }

      const data = await res.json();

      // guardar JWT
      localStorage.setItem('jwt', data.token);

      // actualizar estado logged-in
      setUser(data.user);

      // guardar usuario en localStorage
      localStorage.setItem('user', JSON.stringify(data.user));

      // Navigate to dashboard
      onNavigate('dashboard');

    } catch (err) {
      console.error(err);
      setError(err.message);
      
      // Reset captcha if it was invalid
      if (!disableTurnstile && !err.message && window.turnstile && turnstileWidgetId) {
        window.turnstile.reset(turnstileWidgetId);
        setTurnstileToken(null);
      }
    } finally {
      setLoading(false);
    }
  };
  const handleRecover = async () => {
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: recoverEmail,
      });

      if (!res.ok) throw new Error('Correo inválido');
      setRecoverMessage('Revisa tu correo para restablecer la contraseña');
    } catch (err) {
      setRecoverMessage(err.message);
    }
  };


  return (

    <Box
      sx={{
        width: '100vw',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: { xs: 'flex-start', md: 'center' },
        px: 2,
        py: {xs: 4, md: 0}

      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '1000px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' },
          alignItems: 'center',
          gap: { xs: 4, md: 0 }
        }}
      >

        {/* LOGO */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            order: { xs: 2, md: 0}
          }}
        >
          <img
            src="/gosen_logo_final.png"
            alt="Logo"
            style={{
              width: '100%',
              maxWidth: '350px',
              height: 'auto'
            }}
          />
        </Box>

        {/* FORMA */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            order: { xs: 1, md: 0 }
          }}
        >
          <Card sx={{
            width: '100%',
            maxWidth: { xs: '350px', md: '450px'},
            backgroundColor: '#f5f5f5',
            p: { xs: '1.3rem', md: '1.5rem' }
          }}>

            <CardContent>
              <Typography variant="h5" gutterBottom textAlign="center"
                sx={{ mb: 3, color: '#144272', fontWeight: 'normal' }}>
                Iniciar sesión
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <form onSubmit={handleSubmit}>
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField
                    label="Correo Electrónico"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                  <TextField
                    label="Contraseña"
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange}
                    required
                  />
                  {/* hacer visible el captcha */}
                  {disableTurnstile ? null : <div ref={captchaRef} />}
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      backgroundColor: '#0A2647',
                      color: '#fff',
                      '&:hover': { backgroundColor: '#144272' },
                      mt: 2
                    }}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Entrar'}
                  </Button>

                  <Button
                    onClick={() => setOpenForgot(true)}
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    ¿Olvidaste tu contraseña?
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Box>

        {/* DIALOGO */}

        <Dialog open={openForgot} onClose={() => setOpenForgot(false)}>
          <DialogTitle>Recuperar contraseña</DialogTitle>
          <DialogContent>
            <TextField
              label="Correo"
              type="email"
              fullWidth
              value={recoverEmail}
              onChange={(e) => setRecoverEmail(e.target.value)}
              sx={{ mt: 1 }}
            />
            {recoverMessage && <Alert severity="info" sx={{ mt: 2 }}>{recoverMessage}</Alert>}
            
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenForgot(false)}>Cancelar</Button>
            <Button color="primary" onClick={handleRecover}>Enviar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>

  );
};

export default Login;
